<template>
  <div
    v-accessible-dialog="{ open: registro, close: closeRegister }"
    :class="registro ? '' : 'oculto'"
    class="transition-all fixed sm:py-8 inset-0 bg-[#FAFDFF] sm:bg-black sm:bg-opacity-50 z-50 flex items-center justify-center"
  >
    <section v-if="registro"
      class="max-h-screen overflow-y-auto u-container py-0 overflow-x-hidden bg-white sm:rounded-3xl sm:border sm:shadow-md grid xl:grid-cols-2"
    >
      <aside class="py-20 bg-[#F2F8FF] hidden xl:block">
        <h2 class="font-normal text-center mb-20">
          Vamos a <span class="font-semibold">registrarnos ...</span>
        </h2>
        <figure>
          <img
            width="562"
            height="423"
            src="../../../assets/images/registro-aside.svg"
            class="mx-auto"
            alt="Icono de Sendi"
          />
        </figure>
      </aside>

      <article class="py-20 bg-[#FAFDFF] relative">
        <div
          v-if="googleLoading"
          class="absolute inset-0 bg-white/60 z-50 flex items-center justify-center"
        >
          <div class="flex items-center gap-3">
            <span class="h-6 w-6 rounded-full border-2 border-gray-300 border-t-mainBlue animate-spin"></span>
            <span class="text-sm font-semibold text-gray-600">Procesando...</span>
          </div>
        </div>  

        <div class="sm:w-8/12 mx-auto">
          <!-- TITULO -->
          <h2 class="font-semibold text-center mb-4 text-4xl">Crear una cuenta</h2>
          <p class="text-sm text-center mb-6">Regístrate gratis para obtener tu dirección en Miami.</p>

          <!-- GOOGLE/FACEBOOK -->
          <div class="grid grid-cols-2 items-center gap-4">
            <gmail-component
              @loading="googleLoading = $event"
              @ui-message="({ text, type }) => showUIMessage(text, type)"
            />
            <facebook-component />
          </div>

          <!-- SEPARADORES -->
          <div class="my-6 flex items-center gap-4">
            <hr class="w-full" />
            <span class="h-4 w-8 rounded-full border-2 border-gray-400"></span>
            <hr class="w-full" />
          </div>

          <!-- MENSAJE UI (visible dentro del modal) -->
          <div
            role="alert" v-if="uiMessage.show"
            class="mb-4 px-4 py-3 rounded-lg text-sm font-medium"
            :class="{
              'bg-red-100 text-red-700 border border-red-300': uiMessage.type === 'error',
              'bg-green-100 text-green-700 border border-green-300': uiMessage.type === 'success',
              'bg-yellow-100 text-yellow-700 border border-yellow-300': uiMessage.type === 'warning',
            }"
          >
            {{ uiMessage.text }}
          </div>

          <!-- INPUTS -->
          <form autocomplete="on" @submit.prevent="registerUser">
            <!-- NOMBRE -->
            <label class="relative grid mb-4">
              <span
                class="absolute px-2 -top-2 left-3 bg-[#FAFDFF] text-gray-400 text-xs"
                >Nombre</span
              >
              <input
                :class="error.name ? 'ring ring-mainRed' : ''"
                class="input text-sm w-full font-semibold"
                type="text"
                placeholder="Ingrese su nombre" autocomplete="given-name" required :aria-invalid="error.name || undefined"
                v-model="dataRegister.name"
              />
            </label>

            <!-- APELLIDOS -->
            <label class="relative grid mb-4">
              <span
                class="absolute px-2 -top-2 left-3 bg-[#FAFDFF] text-gray-400 text-xs"
                >Apellidos</span
              >
              <input
                :class="error.surnames ? 'ring ring-mainRed' : ''"
                class="input text-sm w-full font-semibold"
                type="text"
                placeholder="Ingrese su apellido" autocomplete="family-name" required :aria-invalid="error.surnames || undefined"
                v-model="dataRegister.surnames"
              />
            </label>

            <!-- CELULAR -->
            <label class="relative grid mb-4">
              <span
                class="absolute px-2 -top-2 left-3 bg-[#FAFDFF] text-gray-400 text-xs"
                >Celular</span
              >
              <input
                :class="error.telefono ? 'ring ring-mainRed' : ''"
                class="input text-sm w-full font-semibold"
                type="tel"
                placeholder="999 999 999" autocomplete="tel-national" required :aria-invalid="error.telefono || undefined"
                v-model="dataRegister.telefono"
              />
            </label>

            <!-- CORREO -->
            <label class="relative grid mb-4">
              <span
                class="absolute px-2 -top-2 left-3 bg-[#FAFDFF] text-gray-400 text-xs"
                >Correo electrónico</span
              >
              <input
                :class="error.email ? 'ring ring-mainRed' : ''"
                class="input text-sm w-full font-semibold"
                type="email"
                placeholder="Ingrese su correo" autocomplete="email" required :aria-invalid="error.email || undefined"
                v-model="dataRegister.email"
              />
            </label>

            <!-- CONTRASEÑA -->
            <label class="relative grid mb-12 sm:mb-6">
              <span
                class="absolute px-2 -top-2 left-3 bg-[#FAFDFF] text-gray-400 text-xs"
                >Contraseña</span
              >
              <input
                :class="error.password ? 'ring ring-mainRed' : ''"
                class="input text-sm w-full font-semibold"
                :type="style.passworHide ? 'text' : 'password'"
                placeholder="Ingrese su contraseña" autocomplete="new-password" minlength="8" required aria-describedby="register-password-help" :aria-invalid="error.password || undefined"
                v-model="dataRegister.password"
              />
              <div class="absolute right-4 bottom-6 text-gray-400 bg-white-300">
                <button type="button" @click="statusHidePassword" class="sendi-password-toggle" :aria-label="style.passworHide ? 'Ocultar contraseña' : 'Mostrar contraseña'" :aria-pressed="style.passworHide"><img src="../../../assets/icons/icon-watch.svg" alt="" aria-hidden="true" /></button>
              </div>
            </label>

            <p id="register-password-help" class="text-sm mb-4 text-gray-700">Usa una contraseña de al menos 8 caracteres.</p>
            <p class="text-sm mb-6 text-gray-700">Consulta nuestros <a class="underline text-secondary-blue" href="https://sendi.com.pe/terminos-y-condiciones/" target="_blank" rel="noopener">términos y condiciones</a> y la <a class="underline text-secondary-blue" href="https://sendi.com.pe/politica-de-privacidad/" target="_blank" rel="noopener">política de privacidad</a>.</p>
            <!-- BUTTON -->
            <button
              type="submit"
              class="btn btn-blue w-full py-5 mb-4"
              :disabled="loading || googleLoading"
              :class="(loading || googleLoading) ? 'opacity-60 cursor-not-allowed' : ''"
            >
              {{ loading ? "Creando cuenta..." : "Crear cuenta" }}
            </button>
          </form>
        </div>

        <button type="button" @click.prevent="closeRegister" class="sendi-dialog-close" aria-label="Cerrar ventana"><img src="../../../assets/icons/icon-exit-login.svg" alt="" aria-hidden="true" /></button>
      </article>
    </section>
  </div>

  <!-- MODAL VERIFICACIÓN -->
  <div
    v-accessible-dialog="{ open: verification, close: closeModalVerification }"
    :class="verification ? '' : 'hidden'"
    class="fixed h-screen inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center transition-opacity delay-400"
  >
    <div
      class="mx-4 bg-[#FAFAFA] px-8 py-24 xl:py-52 xl:px-36 rounded-2xl relative"
    >
      <div>
        <h2 class="text-2xl xl:text-4xl text-center font-semibold mb-6">
          Verifica tu correo
        </h2>
        <p class="mb-6 text-center text-sm xl:text-base">
          Esta acción requiere una verificación de correo.
          <br class="hiddensm:block" />
          Por favor revisa tu buzón de correo y <br class="hidden sm:block" />
          sigue las instrucciones enviadas.
        </p>
        <p class="mb-6 text-center">El correo fue enviado a:</p>
        <p class="font-semibold text-base xl:text-lg mb-8 text-center">
          {{ email2 }}
        </p>

        <button
          @click.prevent="repetitionVerification"
          class="w-full block bg-mainBlue py-6 text-white font-bold rounded-2xl"
          :disabled="loadingResend"
          :class="loadingResend ? 'opacity-60 cursor-not-allowed' : ''"
        >
          {{ loadingResend ? "Reenviando..." : "Reenviar" }}
        </button>
      </div>

      <button type="button" @click.prevent="closeModalVerification" class="sendi-dialog-close" aria-label="Cerrar ventana"><img src="../../../assets/icons/icon-exit-login.svg" alt="" aria-hidden="true" /></button>
    </div>
  </div>
</template>

<script>
import facebookComponent from "../../global/autentificacion/redes/facebookComponent.vue";
import gmailComponent from "../../global/autentificacion/redes/gmailComponent.vue";
import { server } from "../../../config/global";
import axios from "axios";

const BASE_URL = server.API_URL;

export default {
  components: { facebookComponent, gmailComponent },
  props: ["registro"],
  data() {
    return {
      codeInfluencer: this.$route.query.invitation,
      idUser: 0,
      email2: "",
      verification: false,
      loading: false,
      loadingResend: false,
      googleLoading: false,
      style: {
        passworHide: false,
      },
      uiMessage: {
        show: false,
        type: "error", // error | success | warning
        text: "",
      },
      dataRegister: {
        name: "",
        surnames: "",
        email: "",
        password: "",
        telefono: "",
      },
      error: {
        name: false,
        surnames: false,
        email: false,
        password: false,
        telefono: false,
      },
    };
  },
  methods: {
    // -----------------------------
    // Alerts globales
    // -----------------------------
    showAlert(titulo, description) {
      this.$store.state.alert.titulo = titulo;
      this.$store.state.alert.description = description;
      this.$store.state.alert.estado = true;
    },

    // -----------------------------
    // Mensaje visible en el modal
    // -----------------------------
    showUIMessage(text, type = "error") {
      this.uiMessage.text = text;
      this.uiMessage.type = type;
      this.uiMessage.show = true;
    },
    clearUIMessage() {
      this.uiMessage.show = false;
      this.uiMessage.text = "";
    },

    // -----------------------------
    // Mapeo de errores de registro
    // -----------------------------
    handleRegisterError(error) {
      const status = error?.response?.status;
      const backendMsg = error?.response?.data?.message;
      const errors = error?.response?.data?.errors;

      // Marca campos si el backend devolvió errores tipo 422 { errors: { email: [...] } }
      if (status === 422 && errors && typeof errors === "object") {
        Object.keys(errors).forEach((field) => {
          if (this.error[field] !== undefined) {
            this.error[field] = true;
          }
        });
      }

      let msg = "Error al registrarse. Inténtalo más tarde.";
      let type = "error";

      switch (status) {
        case 409:
          msg = backendMsg || "Este correo ya está registrado.";
          type = "warning";
          break;
        case 422:
          msg = "Datos inválidos. Revisa los campos resaltados.";
          type = "error";
          break;
        case 502:
          msg = "No se pudo crear tu casillero. Inténtalo más tarde.";
          type = "error";
          break;
        case 404:
          msg = "Servicio no disponible. Inténtalo más tarde.";
          type = "error";
          break;
        default:
          // Si es error de red (sin response)
          if (!error?.response) {
            msg = "Problema de conexión. Revisa tu internet e inténtalo otra vez.";
            type = "error";
          }
          break;
      }

      this.showUIMessage(msg, type);
      // Mantén también tu alerta global
      this.showAlert(type === "success" ? "Éxito" : "Error", msg);
    },

    // -----------------------------
    // Verificación
    // -----------------------------
    repetitionVerification() {
      if (!this.idUser) return;

      this.loadingResend = true;
      this.clearUIMessage();

      axios
        .get(`${BASE_URL}client/sendVerificationEmail/${this.idUser}`)
        .then((response) => {
          if (response.status === 200) {
            this.showUIMessage("Correo enviado correctamente.", "success");
            this.showAlert("Éxito", "Correo enviado");
          }
        })
        .catch(() => {
          this.showUIMessage("No se pudo reenviar el correo. Inténtalo más tarde.", "error");
          this.showAlert("Error", "Inténtelo más tarde");
        })
        .finally(() => {
          this.loadingResend = false;
        });
    },
    closeModalVerification() {
      this.verification = false;
      this.email2 = "";
      this.idUser = 0;
      this.clearUIMessage();
    },

    // -----------------------------
    // Validaciones (igual que antes)
    // -----------------------------
    validateName(data) {
      if (data.length !== 0) {
        this.error.name = false;
        return true;
      } else {
        this.error.name = true;
        return false;
      }
    },
    validateSurname(data) {
      if (data.length !== 0) {
        this.error.surnames = false;
        return true;
      } else {
        this.error.surnames = true;
        return false;
      }
    },
    validateTelefono(data) {
      if (data.length !== 0) {
        this.error.telefono = false;
        return true;
      } else {
        this.error.telefono = true;
        return false;
      }
    },
    validateEmail(data) {
      if (data.length !== 0) {
        var expReg =
          /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
        var resultado = expReg.test(data);
        if (resultado === true) {
          this.error.email = false;
          this.email2 = data;
          return true;
        } else {
          this.showUIMessage("Ingrese un correo válido.", "warning");
          this.showAlert("Validación", "Ingrese un correo");
          this.error.email = true;
          return false;
        }
      } else {
        this.error.email = true;
        return false;
      }
    },
    validatePassword(data) {
      if (data.length !== 0) {
        if (data.length >= 8) {
          this.error.password = false;
          return true;
        } else {
          this.showUIMessage("La contraseña debe tener mínimo 8 caracteres.", "warning");
          this.showAlert("Validación", "La contraseña es mayor o igual a 8 dígitos");
          this.error.password = true;
          return false;
        }
      } else {
        this.error.password = true;
        return false;
      }
    },
    validateFormRegister() {
      const name = this.dataRegister.name;
      const surnames = this.dataRegister.surnames;
      const email = this.dataRegister.email;
      const password = this.dataRegister.password;
      const telefono = this.dataRegister.telefono;

      const resultName = this.validateName(name);
      const resultSurname = this.validateSurname(surnames);
      const resultEmail = this.validateEmail(email);
      const resultPassword = this.validatePassword(password);
      const resultTelefono = this.validateTelefono(telefono);

      if (!(resultName && resultSurname && resultEmail && resultPassword && resultTelefono)) {
        this.showUIMessage("Revisa los campos marcados en rojo.", "warning");
      }

      return resultName && resultSurname && resultEmail && resultPassword && resultTelefono;
    },

    // -----------------------------
    // Registro
    // -----------------------------
    registerUser() {
      if (!this.validateFormRegister()) return;

      this.loading = true;
      this.clearUIMessage();

      axios
        .post(`${BASE_URL}client/create-client`, {
          name: this.dataRegister.name,
          surnames: this.dataRegister.surnames,
          email: this.dataRegister.email,
          password: this.dataRegister.password,
          store_id: 1,
          influencer_code: this.codeInfluencer,
          cellphone: "+51" + this.dataRegister.telefono,
        })
        .then((response) => {
          if (response.status === 200 && response.data?.body?.id) {
            this.idUser = response.data.body.id;
            this.verification = true;
            window.localStorage.removeItem("open");
            this.showUIMessage("Cuenta creada. Revisa tu correo para verificar.", "success");
            this.closeRegister(); // mantiene tu flujo
          } else if (response.status === 200) {
            window.localStorage.removeItem("open");
            this.showUIMessage("Cuenta creada. Revisa tu correo para verificar.", "success");
            this.closeRegister();
          }
        })
        .catch((error) => {
          window.localStorage.removeItem("open");

          this.handleRegisterError(error);

          // Limpia formulario solo si NO es correo duplicado
          if (error?.response?.status !== 409) {
            this.resetdata();
            this.resetDataError();
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },

    // -----------------------------
    // Utilidades
    // -----------------------------
    resetDataError() {
      this.error.name = false;
      this.error.surnames = false;
      this.error.email = false;
      this.error.password = false;
      this.error.telefono = false;
    },
    resetdata() {
      this.dataRegister.name = "";
      this.dataRegister.surnames = "";
      this.dataRegister.email = "";
      this.dataRegister.password = "";
      this.dataRegister.telefono = "";
    },
    closeRegister() {
      this.resetDataError();
      this.resetdata();
      this.$emit("closeRegister", false);
    },
    statusHidePassword() {
      this.style.passworHide = !this.style.passworHide;
    },
  },
  watch: {
    "dataRegister.name": function () {
      this.error.name = false;
      this.clearUIMessage();
    },
    "dataRegister.surnames": function () {
      this.error.surnames = false;
      this.clearUIMessage();
    },
    "dataRegister.telefono": function () {
      this.error.telefono = false;
      this.clearUIMessage();
    },
    "dataRegister.email": function () {
      this.error.email = false;
      this.clearUIMessage();
    },
    "dataRegister.password": function () {
      this.error.password = false;
      this.clearUIMessage();
    },
  },
};
</script>
